/**
 * `TextField` (W1-12 ui-kit-i18n; contract TextFieldProps): a labelled single-line text input. `onChange`
 * reports every keystroke (search boxes, live previews); `onCommit` fires once on Enter or blur when the text
 * changed (or always for a mixed value that was edited). Escape drops the edit and restores the value (a second
 * Escape reaches the global bindings). A mixed value shows "Mixed" as placeholder.
 */

import { useId, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useT } from '../i18n/useT';
import { useTr } from './bridge';
import { cx } from './internal/cx';
import { pendingEditProps } from './internal/layers';
import f from './internal/form.module.css';
import { useDraft } from './internal/useDraft';
import type { FieldValue, TextFieldProps } from './types';
import { m } from './ui.messages';

export interface TextFieldExtraProps {
  readonly className?: string | undefined;
  readonly disabled?: boolean | undefined;
  /** Visually hide the label (it still names the input). */
  readonly hideLabel?: boolean | undefined;
  readonly autoFocus?: boolean | undefined;
  readonly type?: 'text' | 'search' | undefined;
}

/** Current text and mixed flag of a text field value. */
export function textValue(v: string | FieldValue<string>): { readonly text: string; readonly mixed: boolean } {
  if (typeof v === 'string') return { text: v, mixed: false };
  return v.kind === 'value' ? { text: v.value, mixed: false } : { text: '', mixed: true };
}

export function TextField(props: TextFieldProps & TextFieldExtraProps) {
  const t = useT();
  const tr = useTr();
  const id = useId();
  const draft = useDraft();
  const { text, mixed } = textValue(props.value);
  const shown = draft.text ?? text;

  const commit = (): void => {
    const d = draft.text;
    draft.clear();
    if (d !== null && (mixed || d !== text)) props.onCommit(d);
  };
  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape' && draft.text !== null) {
      e.preventDefault();
      e.stopPropagation();
      draft.clear();
      props.onChange?.(text);
    }
  };
  const placeholder = mixed ? t(m.mixed) : props.placeholder === undefined ? undefined : tr(props.placeholder);

  return (
    <div className={cx(f.control, props.className)} data-help-id={props.helpId}>
      <label htmlFor={id} className={cx(f.label, props.hideLabel === true && 'visually-hidden')}>
        {props.label}
      </label>
      <input
        id={id}
        className={f.input}
        type={props.type ?? 'text'}
        value={shown}
        placeholder={placeholder}
        maxLength={props.maxLength}
        disabled={props.disabled}
        autoFocus={props.autoFocus}
        autoComplete="off"
        spellCheck={false}
        data-mixed={mixed && draft.text === null ? 'true' : undefined}
        onChange={(e) => {
          draft.set(e.target.value);
          props.onChange?.(e.target.value);
        }}
        onBlur={commit}
        {...pendingEditProps(draft.text !== null)}
        onKeyDown={onKeyDown}
      />
    </div>
  );
}
